'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { ArrowLeft, ArrowRight, Check, Headphones } from 'lucide-react';
import { getOrCreateSessionId, saveAnswersToSession } from '@/lib/session';

// ─── Types ────────────────────────────────────────────────────────────────────

type QuestionType = 'single' | 'single_with_text' | 'freetext' | 'likert' | 'slider' | 'multiselect' | 'multiselect_with_other';

interface Option {
  id: string;
  label: string;
  emoji?: string;
}

interface Question {
  id: string;
  step: number;
  category: string;
  microcopy: string;
  question: string;
  type: QuestionType;
  options?: Option[];
  placeholder?: string;
  maxLength?: number;
  optionalText?: string;
  optionalTextPlaceholder?: string;
  sliderMin?: number;
  sliderMax?: number;
  sliderLabelMin?: string;
  sliderLabelMax?: string;
  optional?: boolean;
}

// ─── Questions ────────────────────────────────────────────────────────────────

const questions: Question[] = [
  // Q1
  {
    id: 'q1_focus',
    step: 1,
    category: 'Your goals',
    microcopy: 'There are no right answers — just honest ones. This shapes everything blv builds for you.',
    question: 'Over the next 30 days, what matters most to you?',
    type: 'single_with_text',
    options: [
      { id: 'q1_anxiety', label: 'Feeling calmer and more at ease day to day' },
      { id: 'q1_critic', label: 'Building a kinder, more supportive inner voice' },
      { id: 'q1_confident', label: 'Feeling more confident going after my goals' },
      { id: 'q1_clear', label: 'Feeling clearer, more focused, and less overwhelmed' },
      { id: 'q1_sleep', label: 'Sleeping better and switching off at night' },
      { id: 'q1_other', label: 'Other' },
    ],
    optionalTextPlaceholder: 'Tell us a little more…',
  },
  // Q2
  {
    id: 'q2_outcome',
    step: 2,
    category: 'Your vision',
    microcopy: 'This becomes the north star for your personalised practice — the version of you we\'re building towards.',
    question: 'Imagine it\'s 30 days from now and blv has worked beautifully. What does that version of you look and feel like?',
    type: 'freetext',
    placeholder: 'e.g. "I feel confident in meetings", "I wake up without dread", "I actually believe I can do this"…',
    maxLength: 300,
  },
  // Q3
  {
    id: 'q3_attitude',
    step: 3,
    category: 'Your starting point',
    microcopy: 'This helps us set the right tone and pace for your experience.',
    question: 'How do you feel about meditation and apps like this?',
    type: 'single',
    options: [
      { id: 'q3_skeptical', label: 'Skeptical but open to trying' },
      { id: 'q3_curious', label: 'New and curious' },
      { id: 'q3_tried', label: "I've tried a few things but nothing really stuck" },
      { id: 'q3_practice', label: 'I already have a practice and want something more tailored' },
    ],
  },
  // Q4
  {
    id: 'q4_why_now',
    step: 4,
    category: 'Why now',
    microcopy: 'Something brought you here today. Understanding that helps us make this feel relevant from the very first session.',
    question: 'What\'s making this feel important right now?',
    type: 'single_with_text',
    options: [
      { id: 'q4_change', label: 'I\'m going through a change and want to handle it well' },
      { id: 'q4_work', label: 'Work or study is putting pressure on me' },
      { id: 'q4_stuck', label: 'I feel stuck and ready to move forward' },
      { id: 'q4_goal', label: 'I have a goal I\'m ready to go after' },
      { id: 'q4_curious', label: 'I just know something needs to shift' },
    ],
    optionalTextPlaceholder: "Anything else you'd like to share?",
  },
  // Q5
  {
    id: 'q5_calm',
    step: 5,
    category: 'Where you are right now',
    microcopy: 'These three questions help us understand your starting point — not as a diagnosis, just so we can meet you where you are.',
    question: 'Right now, how often do you feel calm and at ease?',
    type: 'likert',
    options: [
      { id: 'q5_none', label: 'Rarely or never' },
      { id: 'q5_some', label: 'Sometimes' },
      { id: 'q5_less_half', label: 'About half the time' },
      { id: 'q5_more_half', label: 'More often than not' },
      { id: 'q5_most', label: 'Most of the time' },
    ],
  },
  // Q6
  {
    id: 'q6_hopeful',
    step: 6,
    category: 'Where you are right now',
    microcopy: 'Keep going — you\'re doing great.',
    question: 'How often do you feel genuinely hopeful or excited about where you\'re heading?',
    type: 'likert',
    options: [
      { id: 'q6_none', label: 'Rarely or never' },
      { id: 'q6_some', label: 'Sometimes' },
      { id: 'q6_less_half', label: 'About half the time' },
      { id: 'q6_more_half', label: 'More often than not' },
      { id: 'q6_most', label: 'Most of the time' },
    ],
  },
  // Q7
  {
    id: 'q7_restored',
    step: 7,
    category: 'Where you are right now',
    microcopy: 'Last of the three — this helps us understand how much energy you\'re starting with.',
    question: 'How often do you wake up feeling ready for the day ahead?',
    type: 'likert',
    options: [
      { id: 'q7_none', label: 'Rarely or never' },
      { id: 'q7_some', label: 'Sometimes' },
      { id: 'q7_less_half', label: 'About half the time' },
      { id: 'q7_more_half', label: 'More often than not' },
      { id: 'q7_most', label: 'Most of the time' },
    ],
  },
  // Q8
  {
    id: 'q8_trust',
    step: 8,
    category: 'Self-belief',
    microcopy: 'Drag the slider to wherever feels most honest right now.',
    question: 'How much do you trust yourself to handle what life throws at you?',
    type: 'slider',
    sliderMin: 0,
    sliderMax: 10,
    sliderLabelMin: 'Not at all',
    sliderLabelMax: 'Completely',
  },
  // Q9
  {
    id: 'q9_reset_confidence',
    step: 9,
    category: 'Self-belief',
    microcopy: 'This helps us calibrate the depth and tone of your resets.',
    question: 'When things feel hard, how quickly do you believe you can find your footing again?',
    type: 'slider',
    sliderMin: 0,
    sliderMax: 10,
    sliderLabelMin: 'Takes a long time',
    sliderLabelMax: 'Pretty quickly',
  },
  // Q10
  {
    id: 'q10_pattern',
    step: 10,
    category: 'What gets in the way',
    microcopy: 'This is the core of your personalised practice — be as honest as you can. Knowing what holds you back is the first step to moving past it.',
    question: 'What tends to get in the way of you feeling your best?',
    type: 'single_with_text',
    options: [
      { id: 'q10_hard', label: 'A voice that tells me I\'m not good enough or doing enough.' },
      { id: 'q10_worry', label: 'Overthinking and worrying about what might go wrong.' },
      { id: 'q10_flat', label: 'Feeling flat, low energy, or struggling to get motivated.' },
      { id: 'q10_tense', label: 'Feeling tense, stressed, or on edge.' },
      { id: 'q10_compare', label: 'Comparing myself to others and feeling behind.' },
      { id: 'q10_replay', label: 'Replaying the day or worrying about tomorrow at night.' },
      { id: 'q10_other', label: 'Other' },
    ],
    optionalTextPlaceholder: 'Tell us what gets in the way for you…',
  },
  // Q11
  {
    id: 'q11_loudest',
    step: 11,
    category: 'What gets in the way',
    microcopy: 'Knowing when it peaks helps us time your tools for maximum effect.',
    question: 'When does this tend to show up most?',
    type: 'single_with_text',
    options: [
      { id: 'q11_morning', label: 'Morning, before the day starts' },
      { id: 'q11_work', label: 'During work or study' },
      { id: 'q11_social', label: 'In social situations' },
      { id: 'q11_evening', label: 'Evenings, replaying the day' },
      { id: 'q11_night', label: 'At night, before sleep' },
      { id: 'q11_always', label: "It feels like it's always there" },
      { id: 'q11_other', label: 'Other' },
    ],
    optionalTextPlaceholder: 'Describe when it tends to show up for you…',
  },
  // Q12
  {
    id: 'q12_experience',
    step: 12,
    category: 'Practice fit',
    microcopy: "We'll meet you exactly where you are — no experience needed.",
    question: 'Have you ever tried meditation or breathwork?',
    type: 'single',
    options: [
      { id: 'q12_never', label: 'No, never' },
      { id: 'q12_few', label: "A few times, nothing really stuck" },
      { id: 'q12_sometimes', label: 'Sometimes, but not regularly' },
      { id: 'q12_practice', label: 'I already have some kind of practice' },
    ],
  },
  // Q13
  {
    id: 'q13_time',
    step: 13,
    category: 'Practice fit',
    microcopy: 'Be realistic — even 10 seconds done consistently beats 10 minutes never done.',
    question: 'Right now, what feels realistic for you most days?',
    type: 'single',
    options: [
      { id: 'q13_10s', label: '10-second quick resets only' },
      { id: 'q13_1m', label: 'Around 1 minute' },
      { id: 'q13_3m', label: '1–3 minutes' },
      { id: 'q13_10m', label: '5–10 minutes' },
      { id: 'q13_10mplus', label: '10 minutes+' },
    ],
  },
  // Q14
  {
    id: 'q14_when',
    step: 14,
    category: 'Practice fit',
    microcopy: 'Select all that apply — we\'ll build your practice around these moments.',
    question: 'When do you most want support from blv?',
    type: 'multiselect_with_other',
    options: [
      { id: 'q14_morning', label: 'Before my day starts' },
      { id: 'q14_work', label: 'During work or study' },
      { id: 'q14_social', label: 'In social situations' },
      { id: 'q14_evening', label: "Evenings when I'm replaying the day" },
      { id: 'q14_sleep', label: 'Right before sleep' },
      { id: 'q14_other', label: 'Other' },
    ],
  },
  // Q15
  {
    id: 'q15_where',
    step: 15,
    category: 'Practice fit',
    microcopy: "This helps us make sure your tools work wherever you actually are.",
    question: 'Where are you usually when you need a reset most?',
    type: 'multiselect_with_other',
    options: [
      { id: 'q15_desk', label: 'At my desk' },
      { id: 'q15_commute', label: 'On my commute' },
      { id: 'q15_home', label: 'At home during the day' },
      { id: 'q15_bed', label: 'In bed at night' },
      { id: 'q15_public', label: 'Out in public / social places' },
      { id: 'q15_other', label: 'Other' },
    ],
  },
  // Q16
  {
    id: 'q16_tone',
    step: 16,
    category: 'Tone & preferences',
    microcopy: "This shapes how blv speaks to you — in scripts, resets, and guidance.",
    question: 'How do you want blv to speak to you?',
    type: 'single',
    options: [
      { id: 'q16_gentle', label: 'Very gentle and soothing' },
      { id: 'q16_warm', label: 'Warm but direct' },
      { id: 'q16_motivational', label: 'Motivational and energising' },
    ],
  },
  // Q17
  {
    id: 'q17_sound',
    step: 17,
    category: 'Tone & preferences',
    microcopy: "We'll use this as the default backdrop for your audio sessions.",
    question: 'Which background sound feels most calming to you?',
    type: 'single',
    options: [
      { id: 'q17_rain', label: 'Soft rain' },
      { id: 'q17_ocean', label: 'Ocean waves' },
      { id: 'q17_ambient', label: 'Slow ambient music' },
      { id: 'q17_piano', label: 'Soft piano' },
      { id: 'q17_none', label: 'No preference' },
    ],
  },
  // Q18
  {
    id: 'q18_voice',
    step: 18,
    category: 'Tone & preferences',
    microcopy: "We'll use this to select the narrator for your personalised audio.",
    question: 'Do you have a voice preference for audio?',
    type: 'single',
    options: [
      { id: 'q18_no_pref', label: 'No preference' },
      { id: 'q18_female', label: 'Typically prefer a female voice' },
      { id: 'q18_male', label: 'Typically prefer a male voice' },
    ],
  },
  // Q19 (optional)
  {
    id: 'q19_situation',
    step: 19,
    category: 'Make this even more "you" (optional)',
    microcopy: "The more specific you are here, the more your practice will feel like it was written just for you. Even one sentence makes a real difference.",
    question: 'Is there a specific goal, situation, or moment you most want blv to help you with?',
    type: 'freetext',
    placeholder: 'e.g. "I want to feel confident before big presentations", "I want to stop second-guessing myself", "I want to actually go after the promotion I\'ve been putting off"…',
    maxLength: 300,
    optional: true,
  },
  // Q20 (optional)
  {
    id: 'q20_wish',
    step: 20,
    category: 'Make this even more "you" (optional)',
    microcopy: "This is the most personal question in the whole questionnaire. Your answer shapes the most meaningful part of your practice.",
    question: 'What do you most want to believe about yourself — and are working towards?',
    type: 'freetext',
    placeholder: 'e.g. "That I am capable of achieving what I set my mind to", "That I deserve to feel confident and calm", "That I can trust myself to figure things out"…',
    maxLength: 300,
    optional: true,
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function QuestionnaireWizard() {
  const router = useRouter();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [animating, setAnimating] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const [hasSavedPlan, setHasSavedPlan] = useState(false);

  useEffect(() => {
    const sid = getOrCreateSessionId();
    setSessionId(sid);
    // Check if a previously generated plan exists in localStorage
    try {
      const savedPlan = localStorage.getItem('blv_full_plan');
      const savedUserId = localStorage.getItem('blv_user_id');
      if (savedPlan && savedUserId) {
        setHasSavedPlan(true);
      }
    } catch {
      // localStorage unavailable
    }
  }, []);

  const question = questions[currentStep];
  const progress = ((currentStep + 1) / questions.length) * 100;

  // ── Answer helpers ──────────────────────────────────────────────────────────

  function getSingleAnswer(): string {
    return (answers[question.id] as string) ?? '';
  }

  function getTextAnswer(): string {
    return (answers[`${question.id}_text`] as string) ?? '';
  }

  function getSliderAnswer(): number {
    const val = answers[question.id];
    return typeof val === 'number' ? val : 5;
  }

  function getMultiAnswer(): string[] {
    const val = answers[question.id];
    return Array.isArray(val) ? val : [];
  }

  function isAnswered(): boolean {
    if (question.optional) return true;
    switch (question.type) {
      case 'single': case'single_with_text': case'likert':
        return !!getSingleAnswer();
      case 'freetext':
        return !!(answers[question.id] as string)?.trim();
      case 'slider':
        return answers[question.id] !== undefined;
      case 'multiselect': case'multiselect_with_other':
        return getMultiAnswer().length > 0;
      default:
        return false;
    }
  }

  function setSingle(optId: string) {
    setAnswers((prev) => ({ ...prev, [question.id]: optId }));
  }

  function setFreeText(val: string) {
    setAnswers((prev) => ({ ...prev, [question.id]: val }));
  }

  function setOptionalText(val: string) {
    setAnswers((prev) => ({ ...prev, [`${question.id}_text`]: val }));
  }

  function setSlider(val: number) {
    setAnswers((prev) => ({ ...prev, [question.id]: val }));
  }

  function toggleMulti(optId: string) {
    const current = getMultiAnswer();
    const updated = current.includes(optId)
      ? current.filter((v) => v !== optId)
      : [...current, optId];
    setAnswers((prev) => ({ ...prev, [question.id]: updated }));
  }

  // ── Navigation ──────────────────────────────────────────────────────────────

  // Initialize slider answer when navigating to a slider question
  useEffect(() => {
    if (question.type === 'slider' && answers[question.id] === undefined) {
      const mid = Math.round(((question.sliderMin ?? 0) + (question.sliderMax ?? 10)) / 2);
      setAnswers((prev) => ({ ...prev, [question.id]: mid }));
    }
  }, [currentStep]);

  function goNext() {
    if (!isAnswered()) return;
    if (currentStep === questions.length - 1) {
      // Save answers to session before navigating
      saveAnswersToSession({ ...answers, session_id: sessionId });
      router.push('/personalised-plan-preview');
      return;
    }
    setAnimating(true);
    setTimeout(() => {
      setCurrentStep((s) => s + 1);
      setAnimating(false);
    }, 200);
  }

  function goBack() {
    if (currentStep === 0) {
      router.push('/landing-welcome');
      return;
    }
    setAnimating(true);
    setTimeout(() => {
      setCurrentStep((s) => s - 1);
      setAnimating(false);
    }, 200);
  }

  // ── Render helpers ──────────────────────────────────────────────────────────

  function renderSingle(withOptionalText = false) {
    const selected = getSingleAnswer();
    // For Q10/Q11: show text field only when 'Other' is selected
    // For Q1/Q4: show text field for all selections
    const isOtherSelected = selected.endsWith('_other');
    const showTextField = withOptionalText && selected && (
      question.id === 'q1_focus' || question.id === 'q4_why_now' || isOtherSelected
    );
    return (
      <div className="space-y-3">
        {question.options?.map((opt) => (
          <button
            key={opt.id}
            onClick={() => setSingle(opt.id)}
            className={`answer-card ${selected === opt.id ? 'selected' : ''}`}
          >
            {opt.emoji && (
              <span className="text-xl flex-shrink-0 w-8 text-center">{opt.emoji}</span>
            )}
            <span className="text-sm sm:text-base leading-snug">{opt.label}</span>
            {selected === opt.id && (
              <span className="ml-auto flex-shrink-0">
                <Check size={16} className="text-[#2A9D8F]" />
              </span>
            )}
          </button>
        ))}
        {/* For Q1: show the "tell us more" hint even before any selection */}
        {withOptionalText && question.id === 'q1_focus' && (
          <div className="mt-4">
            <p className="text-xs text-[#9B9590] mb-2 leading-relaxed">
              <span className="font-medium text-[#6B6560]">Want to make this even more personal?</span>{' '}
              The more specific you are, the more tailored your practice becomes — e.g. "I want to feel calmer before job interviews" or "I want to stop spiralling when I make mistakes at work."
            </p>
            <textarea
              value={getTextAnswer()}
              onChange={(e) => setOptionalText(e.target.value)}
              placeholder={question.optionalTextPlaceholder ?? 'Tell us a little more… (optional)'}
              rows={2}
              className="w-full bg-white border border-[#D5D0CA] rounded-xl px-4 py-3 text-sm text-[#1C1917] placeholder-[#C5C0BA] focus:outline-none focus:border-[#2A9D8F] focus:ring-2 focus:ring-[#2A9D8F]/10 transition-all resize-none"
            />
            <p className="text-xs text-[#9B9590] mt-1.5 italic">Optional — skip if you prefer.</p>
          </div>
        )}
        {/* For Q10/Q11: show text field only when 'Other' is selected */}
        {withOptionalText && isOtherSelected && question.id !== 'q1_focus' && (
          <div className="mt-4">
            <textarea
              value={getTextAnswer()}
              onChange={(e) => setOptionalText(e.target.value)}
              placeholder={question.optionalTextPlaceholder ?? 'Tell us more…'}
              rows={2}
              className="w-full bg-white border border-[#D5D0CA] rounded-xl px-4 py-3 text-sm text-[#1C1917] placeholder-[#C5C0BA] focus:outline-none focus:border-[#2A9D8F] focus:ring-2 focus:ring-[#2A9D8F]/10 transition-all resize-none"
            />
            <p className="text-xs text-[#9B9590] mt-1.5 leading-relaxed">
              <span className="italic">Optional — but the more specific you are, the more tailored your practice becomes.</span>
            </p>
          </div>
        )}
        {/* For Q4: show text field for all selections */}
        {showTextField && question.id === 'q4_why_now' && (
          <div className="mt-4">
            <textarea
              value={getTextAnswer()}
              onChange={(e) => setOptionalText(e.target.value)}
              placeholder={question.optionalTextPlaceholder ?? 'Anything else to add? (optional)'}
              rows={2}
              className="w-full bg-white border border-[#D5D0CA] rounded-xl px-4 py-3 text-sm text-[#1C1917] placeholder-[#C5C0BA] focus:outline-none focus:border-[#2A9D8F] focus:ring-2 focus:ring-[#2A9D8F]/10 transition-all resize-none"
            />
            <p className="text-xs text-[#9B9590] mt-1.5 leading-relaxed">
              <span className="italic">Optional — but the more specific you are, the more tailored your practice becomes.</span>
            </p>
          </div>
        )}
      </div>
    );
  }

  function renderFreeText() {
    const val = (answers[question.id] as string) ?? '';
    return (
      <div>
        <textarea
          value={val}
          onChange={(e) => setFreeText(e.target.value)}
          placeholder={question.placeholder}
          maxLength={question.maxLength}
          rows={4}
          className="w-full bg-white border border-[#D5D0CA] rounded-xl px-4 py-3 text-base text-[#1C1917] placeholder-[#C5C0BA] focus:outline-none focus:border-[#2A9D8F] focus:ring-2 focus:ring-[#2A9D8F]/10 transition-all resize-none"
        />
        {question.maxLength && (
          <p className="text-xs text-[#9B9590] mt-1.5 text-right">
            {val.length} / {question.maxLength}
          </p>
        )}
        {question.optional && (
          <button
            onClick={goNext}
            className="mt-3 text-sm text-[#9B9590] underline underline-offset-4 hover:text-[#6B6560] transition-colors"
          >
            Skip this question
          </button>
        )}
      </div>
    );
  }

  function renderLikert() {
    const selected = getSingleAnswer();
    return (
      <div className="space-y-3">
        {question.options?.map((opt) => (
          <button
            key={opt.id}
            onClick={() => setSingle(opt.id)}
            className={`answer-card ${selected === opt.id ? 'selected' : ''}`}
          >
            <span className="text-sm sm:text-base leading-snug">{opt.label}</span>
            {selected === opt.id && (
              <span className="ml-auto flex-shrink-0">
                <Check size={16} className="text-[#2A9D8F]" />
              </span>
            )}
          </button>
        ))}
      </div>
    );
  }

  function renderSlider() {
    const val = getSliderAnswer();
    const min = question.sliderMin ?? 0;
    const max = question.sliderMax ?? 10;
    return (
      <div className="pt-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-[#9B9590]">{question.sliderLabelMin}</span>
          <span className="text-2xl font-bold text-[#2A9D8F]">{val}</span>
          <span className="text-xs text-[#9B9590]">{question.sliderLabelMax}</span>
        </div>
        <input
          type="range"
          min={min}
          max={max}
          value={val}
          onChange={(e) => setSlider(Number(e.target.value))}
          className="w-full accent-[#2A9D8F] h-2 rounded-full cursor-pointer"
          style={{ accentColor: '#2A9D8F' }}
        />
        <div className="flex justify-between mt-1">
          {Array.from({ length: max - min + 1 }, (_, i) => i + min).map((n) => (
            <span key={n} className="text-xs text-[#C5C0BA]" style={{ width: `${100 / (max - min + 1)}%`, textAlign: 'center' }}>
              {n}
            </span>
          ))}
        </div>
        {answers[question.id] === undefined && (
          <p className="text-xs text-[#9B9590] mt-4 text-center italic">
            Move the slider to set your value, then tap Next.
          </p>
        )}
      </div>
    );
  }

  function renderMultiSelect(withOther = false) {
    const selected = getMultiAnswer();
    const otherOptionId = question.options?.find((o) => o.id.endsWith('_other'))?.id ?? '';
    const isOtherSelected = otherOptionId ? selected.includes(otherOptionId) : false;
    return (
      <div className="space-y-3">
        {question.options?.map((opt) => {
          const isSelected = selected.includes(opt.id);
          return (
            <button
              key={opt.id}
              onClick={() => toggleMulti(opt.id)}
              className={`answer-card ${isSelected ? 'selected' : ''}`}
            >
              <span className="text-sm sm:text-base leading-snug">{opt.label}</span>
              {isSelected && (
                <span className="ml-auto flex-shrink-0">
                  <Check size={16} className="text-[#2A9D8F]" />
                </span>
              )}
            </button>
          );
        })}
        {withOther && isOtherSelected && (
          <div className="mt-1">
            <textarea
              value={getTextAnswer()}
              onChange={(e) => setOptionalText(e.target.value)}
              placeholder="Tell us more…"
              rows={2}
              className="w-full bg-white border border-[#D5D0CA] rounded-xl px-4 py-3 text-sm text-[#1C1917] placeholder-[#C5C0BA] focus:outline-none focus:border-[#2A9D8F] focus:ring-2 focus:ring-[#2A9D8F]/10 transition-all resize-none"
            />
            <p className="text-xs text-[#9B9590] mt-1.5 leading-relaxed">
              <span className="italic">Optional — but the more specific you are, the more tailored your practice becomes.</span>
            </p>
          </div>
        )}
        <p className="text-xs text-[#9B9590] mt-2 italic">Select all that apply.</p>
      </div>
    );
  }

  function renderQuestion() {
    switch (question.type) {
      case 'single':
        return renderSingle(false);
      case 'single_with_text':
        return renderSingle(true);
      case 'freetext':
        return renderFreeText();
      case 'likert':
        return renderLikert();
      case 'slider':
        return renderSlider();
      case 'multiselect':
        return renderMultiSelect(false);
      case 'multiselect_with_other':
        return renderMultiSelect(true);
      default:
        return null;
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-[#FAF8F5]/95 backdrop-blur-md border-b border-[#EAE6E0]">
        <div className="max-w-screen-sm mx-auto px-6 h-16 flex items-center justify-between">
          <Link href="/landing-welcome" className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="font-bold text-lg text-[#1C1917]">blv</span>
          </Link>
          <span className="text-sm text-[#9B9590] font-medium">
            {currentStep + 1} of {questions.length}
          </span>
        </div>
        {/* Progress bar */}
        <div className="h-1 bg-[#EAE6E0]">
          <div
            className="h-full bg-gradient-to-r from-[#2A9D8F] to-[#52B788] transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Return to plan banner */}
      {hasSavedPlan && (
        <div className="fixed top-[65px] left-0 right-0 z-40 bg-[#2A9D8F] px-4 py-2.5 flex items-center justify-center gap-3">
          <Headphones size={15} className="text-white flex-shrink-0" />
          <p className="text-sm text-white font-medium">You have a saved plan</p>
          <Link
            href="/your-reset-practice-home"
            className="text-sm font-bold text-white underline underline-offset-2 hover:text-white/80 transition-colors"
          >
            Return to my plan →
          </Link>
        </div>
      )}

      {/* Main content */}
      <main className={`flex-1 ${hasSavedPlan ? 'pt-36' : 'pt-24'} pb-28 px-6 flex items-start justify-center`}>
        <div
          className="w-full max-w-lg"
          style={{
            opacity: animating ? 0 : 1,
            transform: animating ? 'translateY(8px)' : 'translateY(0)',
            transition: 'opacity 0.2s ease, transform 0.2s ease',
          }}
        >
          {/* Category label */}
          <p className="text-xs font-semibold text-[#2A9D8F] uppercase tracking-widest mb-3">
            {question.category}
          </p>

          {/* Question */}
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1C1917] mb-4 leading-tight">
            {question.question}
          </h2>

          {/* Microcopy */}
          <p className="text-sm text-[#9B9590] mb-8 leading-relaxed italic">
            {question.microcopy}
          </p>

          {/* Answer area */}
          {renderQuestion()}
        </div>
      </main>

      {/* Bottom navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#FAF8F5]/95 backdrop-blur-md border-t border-[#EAE6E0] px-6 py-4">
        <div className="max-w-lg mx-auto flex items-center justify-between gap-4">
          <button
            onClick={goBack}
            className="btn-secondary flex items-center gap-2"
            style={{ minHeight: '48px', padding: '0 1.25rem', fontSize: '0.9375rem' }}
          >
            <ArrowLeft size={16} />
            Back
          </button>

          <button
            onClick={goNext}
            disabled={!isAnswered()}
            className="btn-primary flex items-center gap-2"
            style={{ minHeight: '48px', padding: '0 1.25rem', fontSize: '0.9375rem' }}
          >
            {currentStep === questions.length - 1 ? 'See my plan' : 'Next'}
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
